import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../../services/api';
import { compactNumber, ReferenceThumb } from './CreativeTimeline';
import { ActivityIcon, MarkdownText, relativeTime } from './creativeUi';

// Biblioteca de Referências: perfis e posts que o agente já analisou. Tudo aqui é reaproveitado
// sem custo em qualquer conversa.

export function ReferenceLibraryView({ onUse }: { onUse: (text: string) => void }) {
  const [data, setData] = useState<{ profiles: any[]; posts: any[] }>({ profiles: [], posts: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'profiles' | 'posts'>('profiles');
  const [openProfile, setOpenProfile] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);

  const load = async (term = q) => {
    setLoading(true);
    try {
      const res = await api.getReferenceLibrary(term.trim() || undefined);
      setData(res.data);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao carregar a Biblioteca');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(''); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const postsByProfile = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const p of data.posts) {
      if (!p.profileId) continue;
      if (!map.has(p.profileId)) map.set(p.profileId, []);
      map.get(p.profileId)!.push(p);
    }
    for (const list of map.values()) list.sort((a, b) => b.score - a.score);
    return map;
  }, [data.posts]);

  const openPost = async (id: string) => {
    try {
      const res = await api.getReferencePost(id);
      setDetail(res.data);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao abrir a referência');
    }
  };

  const remove = async (kind: 'posts' | 'profiles', id: string) => {
    if (!window.confirm('Remover da Biblioteca? Uma nova análise vai custar créditos de novo.')) return;
    try {
      await api.deleteReferenceItem(kind, id);
      setDetail(null);
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao remover');
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex rounded-xl bg-slate-100 p-1">
          {([['profiles', `Perfis (${data.profiles.length})`], ['posts', `Posts (${data.posts.length})`]] as const).map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)} className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${tab === id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>{label}</button>
          ))}
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por @ ou palavra da legenda…" className="min-w-[180px] flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {loading && data.profiles.length === 0 && data.posts.length === 0 && <p className="text-sm text-slate-400">Carregando…</p>}
        {!loading && data.profiles.length === 0 && data.posts.length === 0 && (
          <div className="mx-auto max-w-md pt-10 text-center">
            <div className="flex justify-center"><ActivityIcon kind="research_profile" /></div>
            <p className="mt-3 text-sm font-semibold text-slate-700">A Biblioteca está vazia</p>
            <p className="mt-1 text-sm text-slate-500">Peça ao agente para analisar um perfil de inspiração ou mande o link de um post. Tudo que ele analisa fica salvo aqui e volta de graça.</p>
          </div>
        )}

        {tab === 'profiles' && (
          <div className="mx-auto max-w-4xl space-y-3">
            {data.profiles.map(p => {
              const posts = postsByProfile.get(p.id) || [];
              const open = openProfile === p.id;
              const stats = p.stats || {};
              return (
                <div key={p.id} className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <ActivityIcon kind="research_profile" />
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-slate-900">@{p.handle} <span className="ml-1 text-xs font-medium text-slate-400">{p.platform}</span></p>
                      <p className="text-xs text-slate-500">{p.postsAnalyzed} posts · média {compactNumber(stats.avgLikes)} curtidas{stats.avgViews ? ` · ${compactNumber(stats.avgViews)} views` : ''} · analisado há {relativeTime(p.analyzedAt)}</p>
                    </div>
                    <button type="button" onClick={() => onUse(`Use o perfil @${p.handle} (já analisado na Biblioteca) como inspiração e crie um conteúdo para a nossa marca.`)} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">Criar a partir dele</button>
                    <button type="button" onClick={() => setOpenProfile(open ? null : p.id)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">{open ? 'Fechar' : 'Ver padrões'}</button>
                  </div>
                  {posts.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto px-4 pb-3">
                      {posts.slice(0, 10).map(post => <ReferenceThumb key={post.id} post={post} onClick={() => openPost(post.id)} />)}
                    </div>
                  )}
                  {open && (
                    <div className="border-t border-slate-100 px-4 py-3">
                      {p.patterns ? <MarkdownText text={p.patterns} className="text-slate-700" /> : <p className="text-sm text-slate-400">Sem análise de padrões.</p>}
                      <button type="button" onClick={() => remove('profiles', p.id)} className="mt-3 text-xs font-semibold text-rose-500 hover:underline">Remover perfil da Biblioteca</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === 'posts' && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(7rem,1fr))] gap-3">
            {data.posts.map(post => (
              <div key={post.id} className="relative">
                <ReferenceThumb post={post} onClick={() => openPost(post.id)} />
                <div className="mt-1 flex flex-wrap gap-1">
                  {post.hasDecupagem && <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[9px] font-bold text-purple-700">decupado</span>}
                  {post.hasVisualAnalysis && <span className="rounded bg-fuchsia-100 px-1.5 py-0.5 text-[9px] font-bold text-fuchsia-700">arte lida</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setDetail(null)}>
          <div className="h-full w-full max-w-lg overflow-y-auto bg-white p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              {detail.thumbnailUrl && <img src={detail.thumbnailUrl} alt="" className="h-28 w-24 rounded-xl object-cover" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-900">{detail.handle ? `@${detail.handle}` : 'Post'} · {detail.format}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {detail.metrics?.views ? `${compactNumber(detail.metrics.views)} views · ` : ''}{compactNumber(detail.metrics?.likes)} curtidas · {compactNumber(detail.metrics?.comments)} comentários
                </p>
                <a href={detail.url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-xs font-semibold text-indigo-600 hover:underline">Abrir original</a>
              </div>
              <button type="button" onClick={() => setDetail(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100" aria-label="Fechar">✕</button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={() => { onUse(`Use esta referência (${detail.url}, já analisada na Biblioteca) para criar um conteúdo para a nossa marca, sem copiar.`); setDetail(null); }} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">Criar a partir deste post</button>
              {['reel', 'tiktok', 'video'].includes(detail.format) && !detail.decupagemDigest && (
                <button type="button" onClick={() => { onUse(`Decupe o vídeo ${detail.url} e me diga o que dá para aproveitar para a nossa marca.`); setDetail(null); }} className="rounded-lg border border-purple-300 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-50">Pedir decupagem</button>
              )}
              <button type="button" onClick={() => remove('posts', detail.id)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-50">Remover</button>
            </div>
            {detail.caption && (
              <section className="mt-5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Legenda</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{detail.caption}</p>
              </section>
            )}
            {detail.visualAnalysis && (
              <section className="mt-5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-fuchsia-500">Leitura da arte</p>
                <MarkdownText text={detail.visualAnalysis} className="mt-1 text-slate-700" />
              </section>
            )}
            {detail.decupagemDigest && (
              <section className="mt-5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-purple-500">Decupagem</p>
                <MarkdownText text={detail.decupagemDigest} className="mt-1 text-slate-700" />
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
